import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, Container} from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const useOnScreen = (ref, rootMargin = "0px") => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      { rootMargin }
    );

    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [ref, rootMargin, hasAnimated]);

  return isVisible;
};

const Featuressec1 = () => {
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const featureRef1 = useRef(null);
  const featureRef2 = useRef(null);
  const featureRef3 = useRef(null);

  const titleVisible = useOnScreen(titleRef);
  const subtitleVisible = useOnScreen(subtitleRef);
  const feature1Visible = useOnScreen(featureRef1);
  const feature2Visible = useOnScreen(featureRef2);
  const feature3Visible = useOnScreen(featureRef3);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  // const scaleIn = {
  //   hidden: { opacity: 0, scale: 0.9 },
  //   visible: {
  //     opacity: 1,
  //     scale: 1,
  //     transition: {
  //       duration: 0.6,
  //       ease: "easeOut",
  //     },
  //   },
  // };

  const features = [
    {
      ref: featureRef1,
      visible: feature1Visible,
      layer: "LAYER 01",
      title: "Data Intelligence Core",
      description:
        "Your AI starts here—even if your data is a mess. This layer creates a trusted, governed foundation that every model depends on.",
      items: [
        "Data contracts + lineage",
        "Policy-based anonymization",
        "Realtime anomaly guardrails",
      ],
      performance: "PERFORMANCE",
      metric: "42K signals / day",
      delay: 0.2,
    },
    {
      ref: featureRef2,
      visible: feature2Visible,
      layer: "LAYER 02",
      title: "Automation Studio",
      description:
        "This is where workflows come alive. Build copilots, orchestrate processes, and connect teams without reinventing the wheel.",
      items: [
        "Reusable policy engine",
        "Human-in-the-loop rooms",
        "Cross-system execution mesh",
      ],
      performance: "PERFORMANCE",
      metric: "1.4k hours released",
      delay: 0.4,
    },
    {
      ref: featureRef3,
      visible: feature3Visible,
      layer: "LAYER 03",
      title: "Decision Intelligence Hub",
      description:
        "Clear, explainable insights for the leaders who need answers fast. Not another BI tool — a real-time narrative of your business.",
      items: [
        "Board-ready storyboards",
        "Scenario planning models",
        "Streaming ROI telemetry",
      ],
      performance: "PERFORMANCE",
      metric: "1.3s time-to-signal",
      delay: 0.6,
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        background: "#FFFFFF",
        py: { xs: 6, md: 4 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        {/* Features Badge */}
        <motion.div
          ref={titleRef}
          initial="hidden"
          animate={titleVisible ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <motion.div
              animate={
                titleVisible
                  ? {
                      boxShadow: [
                        "0 0 0px rgba(64, 122, 255, 0)",
                        "0 0 20px rgba(64, 122, 255, 0.3)",
                        "0 0 0px rgba(64, 122, 255, 0)",
                      ],
                    }
                  : {}
              }
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              style={{ borderRadius: "20px" }}
            >
              <Typography
                sx={{
                  backgroundColor: "#f0f7ff",
                  color: "#1a73e8",
                  px: 3,
                  py: 1,
                  borderRadius: "20px",
                  fontSize: { xs: "12px", md: "14px" },
                  fontWeight: 600,
                  fontFamily: "Poppins",
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                }}
              >
                Features
              </Typography>
            </motion.div>
          </Box>
        </motion.div>

        {/* Main Heading */}
        <motion.div
          ref={subtitleRef}
          initial="hidden"
          animate={subtitleVisible ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: {xs: "left" , md: "center"},
              color: "#1a73e8",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem" },
              mb: 2,
              px: 1,
              lineHeight: 1.3,
            }}
          >
            The system that unifies your data,
            <br />
            automation, and decisions
          </Typography>
        </motion.div>

        {/* Subtitle */}
        <motion.div
          initial="hidden"
          animate={subtitleVisible ? "visible" : "hidden"}
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.7, delay: 0.2, ease: "easeOut" },
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              textAlign: {xs: "left" , md: "center"},
              color: "#546e7a",
              fontFamily: "Poppins",
              fontSize: { xs: "14px", sm: "16px", md: "17px" },
              mb: 8,
              maxWidth: "900px",
              mx: "auto",
              px: 1,
              lineHeight: 1.8,
            }}
          >
            Every business wants AI that actually works. Not experiments. Not
            dashboards no one checks.
            <br />
            This platform gives you a clean data core, automation that scales,
            and clarity for every decision-maker. Start small, expand fast.
          </Typography>
        </motion.div>

        {/* Features Grid with Arrows */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "stretch" },
            justifyContent: "center",
            gap: { xs: 0, md: 0 },
          }}
        >
          {features.map((feature, index) => (
            <React.Fragment key={index}>
              {/* Feature Card */}
              <Box sx={{ flex: 1, maxWidth: { xs: "100%", md: "33%" } }}>
                <motion.div
                  ref={feature.ref}
                  initial="hidden"
                  animate={feature.visible ? "visible" : "hidden"}
                  variants={{
                    hidden: { opacity: 0, y: 50, scale: 0.95 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 0.7,
                        delay: feature.delay,
                        ease: "easeOut",
                      },
                    },
                  }}
                  style={{ height: "100%" }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#FFFFFF",
                      borderRadius: "20px",
                      p: { xs: 3, md: 4 },
                      mx: { xs: 0, md: 1.5 },
                      mb: { xs: 0, md: 0 },
                      height: "100%",
                      border: "1px solid #e0ecff",
                      transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": {
                        transform: "translateY(-12px)",
                        boxShadow: "0 20px 60px rgba(64, 122, 255, 0.15)",
                        borderColor: "#407aff",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background:
                          "linear-gradient(90deg, #407aff, #1a73e8, #407aff)",
                        backgroundSize: "200% 100%",
                        opacity: 0,
                        transition: "opacity 0.4s ease",
                      },
                      "&:hover::before": {
                        opacity: 1,
                        animation: "shimmer 2s infinite linear",
                      },
                      "@keyframes shimmer": {
                        "0%": { backgroundPosition: "200% 0" },
                        "100%": { backgroundPosition: "-200% 0" },
                      },
                    }}
                  >
                    {/* Layer Label */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={
                        feature.visible ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }
                      }
                      transition={{
                        duration: 0.5,
                        delay: feature.delay + 0.3,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#407aff",
                          fontSize: "11px",
                          fontWeight: 700,
                          fontFamily: "Poppins",
                          letterSpacing: "2px",
                          mb: 2,
                          display: "inline-block",
                          backgroundColor: "#f0f7ff",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "6px",
                        }}
                      >
                        {feature.layer}
                      </Typography>
                    </motion.div>

                    {/* Title */}
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={
                        feature.visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }
                      }
                      transition={{
                        duration: 0.5,
                        delay: feature.delay + 0.4,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          color: "#1a73e8",
                          fontFamily: "Poppins",
                          fontWeight: 700,
                          fontSize: { xs: "1.3rem", md: "1.5rem" },
                          mb: 2,
                          lineHeight: 1.3,
                        }}
                      >
                        {feature.title}
                      </Typography>
                    </motion.div>

                    {/* Description */}
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={
                        feature.visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }
                      }
                      transition={{
                        duration: 0.5,
                        delay: feature.delay + 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#546e7a",
                          fontFamily: "Poppins",
                          fontSize: { xs: "13px", md: "14px" },
                          mb: 3,
                          lineHeight: 1.8,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </motion.div>

                    {/* Feature Items */}
                    <Box sx={{ mb: 4, flexGrow: 1 }}>
                      {feature.items.map((item, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={
                            feature.visible
                              ? { opacity: 1, x: 0 }
                              : { opacity: 0, x: -20 }
                          }
                          transition={{
                            duration: 0.4,
                            delay: feature.delay + 0.6 + idx * 0.1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1.5,
                              p: 1,
                              borderRadius: "8px",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "#f0f7ff",
                                transform: "translateX(6px)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg, #407aff, #1a73e8)",
                                mr: 1.5,
                                flexShrink: 0,
                                boxShadow:
                                  "0 0 8px rgba(64, 122, 255, 0.4)",
                              }}
                            />
                            <Typography
                              sx={{
                                color: "#455a64",
                                fontFamily: "Poppins",
                                fontSize: { xs: "12px", md: "13px" },
                                lineHeight: 1.6,
                                fontWeight: 500,
                              }}
                            >
                              {item}
                            </Typography>
                          </Box>
                        </motion.div>
                      ))}
                    </Box>

                    {/* Divider */}
                    <motion.div
                      initial={{ scaleX: 0 }}
                      animate={
                        feature.visible ? { scaleX: 1 } : { scaleX: 0 }
                      }
                      transition={{
                        duration: 0.6,
                        delay: feature.delay + 0.8,
                      }}
                      style={{ originX: 0 }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "2px",
                          background:
                            "linear-gradient(90deg, #407aff, #e0ecff, transparent)",
                          mb: 3,
                        }}
                      />
                    </motion.div>

                    {/* Performance Section */}
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={
                        feature.visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }
                      }
                      transition={{
                        duration: 0.5,
                        delay: feature.delay + 0.9,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            color: "#90a4ae",
                            fontSize: "10px",
                            fontWeight: 700,
                            fontFamily: "Poppins",
                            letterSpacing: "2px",
                            mb: 0.5,
                          }}
                        >
                          {feature.performance}
                        </Typography>
                        <motion.div
                          animate={
                            feature.visible
                              ? {
                                  color: [
                                    "#407aff",
                                    "#1a73e8",
                                    "#407aff",
                                  ],
                                }
                              : {}
                          }
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#407aff",
                              fontFamily: "Poppins",
                              fontWeight: 700,
                              fontSize: {
                                xs: "1.3rem",
                                md: "1.5rem",
                              },
                            }}
                          >
                            {feature.metric}
                          </Typography>
                        </motion.div>
                      </Box>
                    </motion.div>
                  </Box>
                </motion.div>
              </Box>

              {/* Arrow between cards */}
              {index < features.length - 1 && (
                <>
                  {/* Desktop Arrow (horizontal →) */}
                  <Box
                    sx={{
                      display: { xs: "none", md: "flex" },
                      alignItems: "center",
                      justifyContent: "center",
                      px: 0.5,
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={
                        features[index].visible
                          ? { opacity: 1, scale: 1 }
                          : { opacity: 0, scale: 0 }
                      }
                      transition={{
                        duration: 0.5,
                        delay: features[index].delay + 0.5,
                      }}
                    >
                        <Box
                          sx={{
                            width: "44px",
                            height: "44px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #f0f7ff, #e0ecff)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid #407aff",
                            boxShadow:
                              "0 4px 15px rgba(64, 122, 255, 0.2)",
                          }}
                        >
                          <ArrowForwardIcon
                            sx={{
                              color: "#407aff",
                              fontSize: "22px",
                            }}
                          />
                        </Box>
                    </motion.div>
                  </Box>

                  {/* Mobile Arrow (vertical ↓) */}
                  <Box
                    sx={{
                      display: { xs: "flex", md: "none" },
                      alignItems: "center",
                      justifyContent: "center",
                      py: 2,
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={
                        features[index].visible
                          ? { opacity: 1, scale: 1 }
                          : { opacity: 0, scale: 0 }
                      }
                      transition={{
                        duration: 0.5,
                        delay: features[index].delay + 0.5,
                      }}
                    >
                        <Box
                          sx={{
                            width: "44px",
                            height: "44px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #f0f7ff, #e0ecff)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid #407aff",
                            boxShadow:
                              "0 4px 15px rgba(64, 122, 255, 0.2)",
                          }}
                        >
                          <KeyboardArrowDownIcon
                            sx={{
                              color: "#407aff",
                              fontSize: "24px",
                            }}
                          />
                        </Box>
                    </motion.div>
                  </Box>
                </>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Featuressec1;